import { useState, useEffect, useRef } from "react";
import WavesurferPlayer from "@wavesurfer/react";
import { FaPlay, FaPause } from "react-icons/fa";
import Section from "../../components/Section";
import Heading from "../../components/Heading";

const audioData = [
  {
    name: "Clara - Bright Smile Clinic",
    imgSrc: "/logo.png",
    audioSrc: "/audio.mp3",
    description:
      "Hear how our AI Voice Agent politely reminds a client about their upcoming dental appointment.",
    tags: ["Dental", "AI Voice Agent", "Appointment Reminder"],
  },
  {
    name: "Jenny - Sunshine Real Estate",
    imgSrc: "/logo.png",
    audioSrc: "/audio2.mp3",
    description:
      "Discover in this recording how AI instantly provides comprehensive property details.",
    tags: ["Real Estate", "AI", "Property Details"],
  },
  {
    name: "Jordan - CandleScent Eshop",
    imgSrc: "/logo.png",
    audioSrc: "/audio3.mp3",
    description:
      "Simplify and expedite the refund process for your customers with AI-powered agents.",
    tags: ["E-commerce", "Refund", "AI Agent"],
  },
];

const AiVoice = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [wavesurferInstances, setWavesurferInstances] = useState({});
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTimes, setCurrentTimes] = useState({});
  const currentWaveformRef = useRef(null);

  // Auto-slide functionality
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isPlaying) {
        nextSlide();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [isPlaying, currentSlide]);

  // Handle waveform ready
  const onReady = (ws, index) => {
    setWavesurferInstances((prev) => ({ ...prev, [index]: ws }));
    ws.on("audioprocess", () => {
      setCurrentTimes((prev) => ({
        ...prev,
        [index]: ws.getCurrentTime(),
      }));
    });
    ws.on("finish", () => setIsPlaying(false));
  };

  // Play/Pause button handler
  const onPlayPause = () => {
    const currentWs = wavesurferInstances[currentSlide];
    if (currentWs) {
      currentWs.playPause();
      setIsPlaying((prev) => !prev);
    }
  };

  // Reset audio and states when navigating slides
  const resetAudio = () => {
    const currentWs = wavesurferInstances[currentSlide];
    if (currentWs) {
      currentWs.stop();
      setIsPlaying(false);
    }
  };

  const nextSlide = () => {
    resetAudio();
    setCurrentSlide((prev) => (prev + 1) % audioData.length);
  };

  const prevSlide = () => {
    resetAudio();
    setCurrentSlide((prev) => (prev - 1 + audioData.length) % audioData.length);
  };

  const formatTime = (time = 0) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
      2,
      "0",
    )}`;
  };

  return (
    <Section crosses>
      <Heading
        title="Revolutionizing Communication with AI Voice Agents"
        className="md:max-w-xl lg:max-w-2xl"
        headingclassName="!text-2xl sm:!text-3xl lg:!text-4xl font-semibold text-center"
        text="Witness the impact of AI voice agents in real-world scenarios, enhancing efficiency and customer interactions across industries."
        textclassName="!text-slate-300 text-center"
      />
      <div className="relative flex flex-col items-center justify-center py-8">
        {/* Slide Container */}
        <div className="relative mb-10 h-[300px] w-full max-w-[95%] md:h-[400px] md:max-w-[80%] lg:max-w-[70%]">
          {/* Desktop Previous Button */}
          <button
            onClick={prevSlide}
            className="absolute left-2 top-1/2 z-3 hidden -translate-y-1/2 transform text-4xl sm:block"
          >
            &lt;
          </button>

          {/* Slides */}
          <div
            className="flex transition-transform duration-1000 ease-in-out"
            style={{ transform: `translateX(-${currentSlide * 100}%)` }}
          >
            {audioData.map((slide, index) => (
              <div
                key={index}
                className={`flex flex-shrink-0 flex-col items-center justify-center space-y-6 rounded-2xl bg-slate-900 p-3 text-center neon-purple ${
                  currentSlide === index ? "opacity-100" : "opacity-0"
                } w-full transition-opacity duration-1000`}
              >
                <div className="w-[80%] md:w-[60%]">
                  <h2 className="mb-2 text-lg font-bold text-white sm:text-xl">
                    {slide.name}
                  </h2>
                  <div className="mb-4">
                    {slide.tags.map((tag, tagIndex) => (
                      <span
                        key={tagIndex}
                        className="mb-1 mr-1 inline-block rounded-full bg-purple-600 px-2 py-1 text-xs text-black"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <p className="mb-4 px-4 text-xs text-gray-100 sm:text-sm">
                    {slide.description}
                  </p>
                </div>

                {/* Audio Waveform */}
                <div className="relative w-full max-w-[90%] md:max-w-[85%]">
                  <WavesurferPlayer
                    ref={index === currentSlide ? currentWaveformRef : null}
                    height={40}
                    waveColor="purple"
                    progressColor="white"
                    url={slide.audioSrc}
                    onReady={(ws) => onReady(ws, index)}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                  />

                  {/* Play/Pause Button */}
                  <button
                    aria-label="Play Button"
                    onClick={onPlayPause}
                    className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 transform rounded-full bg-purple-600 p-4 text-2xl text-white shadow-md sm:p-5 sm:text-3xl"
                  >
                    {isPlaying ? <FaPause /> : <FaPlay />}
                  </button>
                </div>

                {/* Time Display */}
                <div className="mt-2 flex w-[90%] justify-between text-xs text-gray-400 sm:text-sm">
                  <span>{formatTime(currentTimes[index] || 0)}</span>
                  <span>
                    {formatTime(wavesurferInstances[index]?.getDuration() || 0)}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Next Button */}
          <button
            onClick={nextSlide}
            className="absolute right-2 top-1/2 hidden -translate-y-1/2 transform text-4xl sm:block"
          >
            &gt;
          </button>
        </div>

        {/* Mobile Navigation Buttons */}
        <div className="mb-5 mt-4 flex justify-center space-x-12 sm:hidden">
          <button
            onClick={prevSlide}
            className="rounded-full bg-gray-700 p-2 text-3xl text-white"
          >
            &lt;
          </button>
          <button
            onClick={nextSlide}
            className="rounded-full bg-gray-700 p-2 text-3xl text-white"
          >
            &gt;
          </button>
        </div>

        {/* Dot Pagination */}
        <div className="absolute bottom-0 left-1/2 mb-4 flex -translate-x-1/2 transform space-x-2">
          {audioData.map((_, index) => (
            <button
              aria-label="Dot Pagination"
              key={index}
              onClick={() => {
                resetAudio();
                setCurrentSlide(index);
              }}
              className={`h-4 w-4 rounded-full sm:h-6 sm:w-6 ${
                currentSlide === index ? "bg-blue-500" : "bg-gray-400"
              }`}
            ></button>
          ))}
        </div>
      </div>
    </Section>
  );
};

export default AiVoice;
